import type { ComponentType } from 'react';
import type { BaseModalProps } from './createModalWrapper.types';
import { createElement } from 'react';

/**
 * Creates a modal wrapper that ensures consistent behavior
 *
 * @param Component - The modal component to wrap
 * @param defaultProps - Default props to apply
 * @returns A wrapped component with consistent modal behavior
 */
function createModalWrapper<T extends BaseModalProps>(
  Component: ComponentType<T>,
  defaultProps?: Partial<T>,
): ComponentType<T> {
  const WrappedComponent = (props: T) => {
    const mergedProps = { ...defaultProps, ...props };

    // Validate that Component is a valid React component
    if (!Component || typeof Component !== 'function') {
      console.error('createModalWrapper: Component must be a valid React component');
      return null;
    }

    return createElement(Component, mergedProps);
  };

  WrappedComponent.displayName = `ModalWrapper(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

export default createModalWrapper;
